import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaNodeJs,
  FaReact,
  FaDatabase,
  FaFigma,
  FaJsSquare,
  FaAndroid,
  FaApple,
  FaWindows,
  FaSwift,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight
} from "react-icons/fa";
import {
  SiKotlin,
  SiFlutter,
  SiDart
} from "react-icons/si";
import LogoLoop from "../components/LogoLoop/LogoLoop";
import MobileProgressNavigator from "../components/MobileProgressNavigator";
import './MobileDeveloper.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const MOBILE_PROJECTS = [
  {
    id: "imt-track",
    name: "IMT-Track",
    subtitle: "Workforce Attendance, Payroll & Shift Manager",
    description: "Our comprehensive corporate tracking system managing employee check-ins/outs, OT (overtime) hours, complex overnight shifts, payroll calculations, leaves, and hourly permissions under a hierarchical level-approval tree, equipped with robust admin monitoring consoles.",
    platform: "Android & iOS Native",
    features: [
      "Precise Check-In/Check-Out & Active Streak Management",
      "Overnight Shift Orchestration & Automated OT Calculation",
      "Comprehensive Localized Payroll Processing",
      "Leave & Hourly Permission Workflows with Hierarchical Approvals",
      "Feature-Rich Analytics Dashboard for Administrators"
    ],
    icon: "fa-map-marker-alt",
    color: "#3DDC84",
    images: [
      "/mobile-apps/IMT-track/image-1.png",
      "/mobile-apps/IMT-track/image-2.png",
      "/mobile-apps/IMT-track/image-3.png",
      "/mobile-apps/IMT-track/image-4.png",
      "/mobile-apps/IMT-track/image-5.png",
      "/mobile-apps/IMT-track/image-6.png",
      "/mobile-apps/IMT-track/image-7.png",
      "/mobile-apps/IMT-track/image-8.png"
    ]
  },
  {
    id: "calls-app",
    name: "iMatrix Calls",
    subtitle: "Field Call Allocation & Live Location Tracker",
    description: "An enterprise mobile solution developed to efficiently allocate calls to field employees, track employees' live GPS locations while on active client visits, and empower field agents to instantly add cold calls and client updates directly on the go.",
    platform: "Android Native",
    features: [
      "Seamless Call Allocation to Field Employees",
      "Live GPS Location Tracking during Active Calls",
      "Instant On-Field Cold Call Creation & Client Logging",
      "Comprehensive Trip Monitoring & Agent Allocation Controls",
      "Immediate Sync with Executive CRM Dashboard"
    ],
    icon: "fa-phone-alt",
    color: "#6B7A52",
    images: [
      "/mobile-apps/calls-app/image-1.png",
      "/mobile-apps/calls-app/image-2.png",
      "/mobile-apps/calls-app/image-3.png",
      "/mobile-apps/calls-app/image-4.png",
      "/mobile-apps/calls-app/image-5.png",
      "/mobile-apps/calls-app/image-6.png",
      "/mobile-apps/calls-app/image-7.png",
      "/mobile-apps/calls-app/image-8.png",
      "/mobile-apps/calls-app/image-9.png"
    ]
  },
  {
    id: "logistic",
    name: "iMatrix Logistic",
    subtitle: "GC Management & Transit Master Controller",
    description: "A robust enterprise logistics solution engineered to streamline goods carriage operations. Create and process GC entries, track and update transits in real-time, and manage critical operational masters including consignors, consignees, drivers, trucks, brokers, and branches.",
    platform: "Android Native",
    features: [
      "Goods Consignment (GC) Entry Creation",
      "Real-Time Transit Updates & Tracking",
      "Master Database Management (Consignor & Consignee)",
      "Driver, Truck & Broker Fleet Orchestration",
      "Multi-Branch Network Synchronization"
    ],
    icon: "fa-truck",
    color: "#F2D996",
    images: [
      "/mobile-apps/logistic/image-1.png",
      "/mobile-apps/logistic/image-2.png",
      "/mobile-apps/logistic/image-3.png",
      "/mobile-apps/logistic/image-4.png",
      "/mobile-apps/logistic/image-5.png",
      "/mobile-apps/logistic/image-6.png"
    ]
  },
  {
    id: "e-ware",
    name: "e-Warehouse",
    subtitle: "Tally ERP Voucher Attachment Utility",
    description: "A forward-compatible workflow utility built to elevate accounting document flows. While legacy Tally ERP versions lack built-in attachment features, e-Warehouse provides a seamless QR-based bridge to upload and attach documents/images directly to secure transaction references, designed to be fully future-proof and ready to integrate with upcoming native Tally ERP updates.",
    platform: "Android & iOS (Flutter)",
    features: [
      "Seamless Mobile-to-Tally Attachment Bridge",
      "Voucher Matching via High-Speed QR Scanner",
      "Direct Handshake with Secure Transaction Reference",
      "High-Quality Document & Image Capture",
      "Fully Future-Proofed for Upcoming Native Tally ERP Releases"
    ],
    icon: "fa-boxes",
    color: "#EBE7D9",
    images: [
      "/mobile-apps/e-ware/image-1.jpeg",
      "/mobile-apps/e-ware/image-2.jpeg",
      "/mobile-apps/e-ware/image-3.jpeg",
      "/mobile-apps/e-ware/image-4.jpeg",
      "/mobile-apps/e-ware/image-5.jpeg"
    ]
  },
  {
    id: "voucher-approvals",
    name: "Voucher Approver",
    subtitle: "Hierarchical Transaction Sign-off Portal",
    description: "An advanced enterprise auditing tool built to secure ledger posting. Voucher Approver intercepts draft transactions from 'Optional' states, routing them through customized hierarchy-wise approval levels, before executing a safe conversion into final, regular ledger posting entries.",
    platform: "Cross-Platform Mobile",
    features: [
      "Hierarchy-Wise Transaction Sign-off",
      "Optional-to-Regular Ledger Conversion",
      "Robust Level Approval Workflows",
      "Secure Real-time Tally Integration",
      "Audit Trail & Verification Logs"
    ],
    icon: "fa-file-invoice-dollar",
    color: "#2A2C24",
    images: [
      "/mobile-apps/voucher-approvals/image-1.jpeg",
      "/mobile-apps/voucher-approvals/image-2.jpeg",
      "/mobile-apps/voucher-approvals/image-3.png",
      "/mobile-apps/voucher-approvals/image-4.png",
      "/mobile-apps/voucher-approvals/image-5.png"
    ]
  },
  {
    id: "authenticator",
    name: "Boss-Key",
    subtitle: "Voucher-Level 2FA Security App",
    description: "The ultimate security lock for enterprise accounting. Boss-Key introduces two-factor verification to your accounting books by generating a unique, dynamic OTP (One-Time Password) for each voucher transaction, requiring authorized mobile clearance before any voucher can be successfully saved in Tally.",
    platform: "iOS & Android SDK",
    features: [
      "Unique Voucher-Level OTP Generation",
      "Two-Factor Authentication (2FA) for Tally",
      "Authorized Mobile Clearance Gating",
      "Secures Accounting Books from Unauthorized Postings",
      "Time-Based Dynamic Security Synchronization"
    ],
    icon: "fa-user-shield",
    color: "#3a3f34",
    images: [
      "/mobile-apps/authenticator/image-1.png"
    ]
  }
];

const AutoPhoneMockup = ({ images, appName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!images || images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images, currentIndex]); // Resets the 3-second timer whenever index changes (on manual click too)

  const handlePrev = (e) => {
    e.stopPropagation();
    if (!images || images.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (!images || images.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div className="alt-phone-mockup-wrapper">
      <div className="phone-slider-container">
        {/* Manual navigation arrows outside the phone */}
        {images.length > 1 && (
          <>
            <button className="mockup-arrow prev" onClick={handlePrev} aria-label="Previous screenshot">
              <FaChevronLeft />
            </button>
            <button className="mockup-arrow next" onClick={handleNext} aria-label="Next screenshot">
              <FaChevronRight />
            </button>
          </>
        )}

        <div className="phone-mockup">
          {/* Phone screen containing screenshots with smooth cross-fade */}
          <div className="phone-screen-slider">
            {images.map((img, idx) => (
              <img
                key={img}
                src={img}
                alt={`${appName} Screen ${idx + 1}`}
                className={`phone-screen-img ${idx === currentIndex ? 'active' : ''}`}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Screen indicators underneath */}
      {images.length > 1 && (
        <div className="phone-indicators">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`phone-indicator-dot ${idx === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`Show screen ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MobileDeveloper = () => {
  const navigate = useNavigate();
  const platformSpecs = {
    android: [
      "Kotlin & Android SDK Native Stacks",
      "Dynamic background thread tasks",
      "Material Design 3 system compliance",
      "Google Play Console deployment"
    ],
    ios: [
      "Swift & SwiftUI optimized engines",
      "CoreData & local security encryption",
      "Apple HIG guidelines compatibility",
      "App Store Connect ecosystem setup"
    ],
    windows: [
      "C# & .NET Core high-perf backends",
      "Offline sqlite client caching systems",
      "Fluent UI desktop layout frameworks",
      "Enterprise MSIX package deployment"
    ]
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      }
    }
  }, []);

  return (
    <div className="mobile-page-wrapper">
      {/* Floating progress navigator for Mobile Development projects */}
      <MobileProgressNavigator />

      {/* ── Cinematic Full-Width Header Banner ── */}
      <header className="mobile-header" style={{ backgroundImage: 'linear-gradient(rgba(15, 23, 42, 0.45), rgba(15, 23, 42, 0.65)), url("/mobile-app-cover/mobile-app-dev-cover.png")' }}>
        <div className="mobile-header-overlay-content">
          <h1>Mobile & Windows Application Development</h1>
        </div>
      </header>

      <div className="mobile-page-container mobile-root">

        {/* ── Header Description & Badge Showcase Below Cover ── */}
        <div className="mobile-intro-section">
          <p className="mobile-intro-text">
            Leveraging state-of-the-art technology, we create comprehensive and high-quality applications
            that exceed client expectations. Our developers excel in frameworks such as Android SDK, Kotlin,
            and Swift, ensuring seamless performance across all mobile ecosystems.
          </p>

          <div className="mobile-features-below">
            <div className="mobile-feature-item-below">
              <i className="fas fa-code"></i>
              <h4>Custom Dev</h4>
              <p>Tailored to your business.</p>
            </div>
            <div className="mobile-feature-item-below">
              <i className="fas fa-mobile-alt"></i>
              <h4>Cross-Platform</h4>
              <p>iOS & Android ready.</p>
            </div>
            <div className="mobile-feature-item-below">
              <i className="fas fa-lock"></i>
              <h4>Data Security</h4>
              <p>Robust protection.</p>
            </div>
          </div>
        </div>

      {/* ── Alternating Mobile App Showcase Sections (Run sequentially down the page) ── */}
      <div className="mobile-apps-showcase-list">
        {MOBILE_PROJECTS.map((proj, idx) => {
          const isEven = idx % 2 === 0;
          return (
            <section id={proj.id} key={proj.id} className={`alt-section ${isEven ? 'row-normal' : 'row-reverse'}`}>
              <div className="alt-text">
                <h3>
                  <i className={`fas ${proj.icon}`}></i> {proj.name}
                </h3>
                <h4 className="alt-subtitle">{proj.subtitle}</h4>
                <p>{proj.description}</p>
                <ul className="alt-features-bullets">
                  {proj.features.map(f => (
                    <li key={f}>
                      <FaCheckCircle className="li-bullet" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {proj.id === "logistic" && (
                  <button className="btn-web-link" onClick={() => navigate('/WebDevelopment#logistic-web')}>
                    Check Logistics Web Application <FaArrowRight />
                  </button>
                )}
                {proj.id === "e-ware" && (
                  <button className="btn-web-link" onClick={() => navigate('/WebDevelopment#e-ware-web')}>
                    Check e-Warehouse Web Portal <FaArrowRight />
                  </button>
                )}
              </div>
              <div className="alt-media">
                <AutoPhoneMockup images={proj.images} appName={proj.name} />
              </div>
            </section>
          );
        })}
      </div>

      {/* ── New Platforms Ecosystem Showcase (3 Columns) ── */}
      <section className="mobile-platforms-showcase">
        <div className="showcase-header">
          <span className="tagline">Diverse Targets</span>
          <h2>Native Platform Specialization</h2>
          <p>We build with premium native architectures and design protocols optimized for each operating system.</p>
        </div>

        <div className="platforms-grid">
          {/* Card 1: Android */}
          <div className="platform-card">
            <div className="platform-card-header android">
              <FaAndroid className="platform-icon" />
              <h3>Android Development</h3>
            </div>
            <div className="platform-card-body">
              <p>Highly robust Android engineering deploying Kotlin-first code architecture built to perform and scale.</p>
              <div className="spec-title">Technical Specifications:</div>
              <ul className="spec-list">
                {platformSpecs.android.map(s => <li key={s}><FaCheckCircle className="li-bullet" /> {s}</li>)}
              </ul>
            </div>
          </div>

          {/* Card 2: iOS */}
          <div className="platform-card">
            <div className="platform-card-header ios">
              <FaApple className="platform-icon" />
              <h3>iOS Development</h3>
            </div>
            <div className="platform-card-body">
              <p>Premium Apple ecosystems Swift development optimized for maximum speed and beautiful fluidity.</p>
              <div className="spec-title">Technical Specifications:</div>
              <ul className="spec-list">
                {platformSpecs.ios.map(s => <li key={s}><FaCheckCircle className="li-bullet" /> {s}</li>)}
              </ul>
            </div>
          </div>

          {/* Card 3: Windows */}
          <div className="platform-card">
            <div className="platform-card-header windows">
              <FaWindows className="platform-icon" />
              <h3>Windows Development</h3>
            </div>
            <div className="platform-card-body">
              <p>Powerful desktop client architectures leveraging .NET technologies for offline database operations.</p>
              <div className="spec-title">Technical Specifications:</div>
              <ul className="spec-list">
                {platformSpecs.windows.map(s => <li key={s}><FaCheckCircle className="li-bullet" /> {s}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── New Enterprise Capability Matrix ── */}
      {/* <section className="mobile-matrix-section">
        <div className="showcase-header text-center-wrapper">
          <span className="tagline">Feature Comparisons</span>
          <h2>Platform Capability Matrix</h2>
          <p>A detailed view of offline, sync, and core features optimized across native devices.</p>
        </div>

        <div className="matrix-table-wrapper">
          <table className="capability-matrix-table">
            <thead>
              <tr>
                <th>System Capabilities</th>
                <th className="text-center"><FaAndroid /> Android</th>
                <th className="text-center"><FaApple /> iOS</th>
                <th className="text-center"><FaWindows /> Windows</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Offline Local Databases</strong> (SQLite, Realm, CoreData)</td>
                <td className="text-center text-green">✔ Full Support</td>
                <td className="text-center text-green">✔ Full Support</td>
                <td className="text-center text-green">✔ Full Support</td>
              </tr>
              <tr>
                <td><strong>Background GPS Tracking & Fencing</strong></td>
                <td className="text-center text-green">✔ Full Support</td>
                <td className="text-center text-green">✔ Full Support</td>
                <td className="text-center text-muted">Limited (Cellular only)</td>
              </tr>
              <tr>
                <td><strong>Push Notification Channels</strong> (FCM, APNs, WNS)</td>
                <td className="text-center text-green">✔ Native</td>
                <td className="text-center text-green">✔ Native</td>
                <td className="text-center text-green">✔ Native</td>
              </tr>
              <tr>
                <td><strong>Biometric Authentication</strong> (Face ID & Fingerprint)</td>
                <td className="text-center text-green">✔ Bio API</td>
                <td className="text-center text-green">✔ Touch/FaceID</td>
                <td className="text-center text-muted">Windows Hello Support</td>
              </tr>
              <tr>
                <td><strong>Local Encryption Key Stores</strong></td>
                <td className="text-center text-green">✔ Android KeyStore</td>
                <td className="text-center text-green">✔ Keychain API</td>
                <td className="text-center text-green">✔ DPAPI Encryption</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Technology Logo Ticker Section ── */}
      <section className="web-tech-section">
        <div className="web-tech-content">
          <div className="tech-badge">Cutting-Edge Mobile Stacks</div>
          <h2>Our Core Technology Stack</h2>
          <p>
            We program robust business applications using standard native environments and elite cross-platform ecosystems
            for maximum reliability and operational longevity.
          </p>

          <div className="tech-loop-container">
            <LogoLoop
              logos={[
                { node: <FaAndroid style={{ color: '#3DDC84' }} />, title: "Android" },
                { node: <FaApple style={{ color: '#555555' }} />, title: "iOS" },
                { node: <SiKotlin style={{ color: '#7F52FF' }} />, title: "Kotlin" },
                { node: <FaSwift style={{ color: '#F05138' }} />, title: "Swift" },
                { node: <SiFlutter style={{ color: '#02569B' }} />, title: "Flutter" },
                { node: <FaReact style={{ color: '#61DAFB' }} />, title: "React Native" },
                { node: <FaWindows style={{ color: '#0078D6' }} />, title: "Windows" },
                { node: <FaNodeJs style={{ color: '#339933' }} />, title: "Node.js" },
                { node: <FaJsSquare style={{ color: '#323330', backgroundColor: '#F7DF1E', borderRadius: '4px' }} />, title: "JavaScript" },
                { node: <FaDatabase style={{ color: '#4479A1' }} />, title: "SQL Database" },
                { node: <FaFigma style={{ color: '#F24E1E' }} />, title: "Figma" },
              ]}
              speed={50}
              gap={80}
              logoHeight={50}
              direction="left"
              fadeOut={false}
              pauseOnHover={true}
              scaleOnHover={true}
            />
          </div>
        </div>

        <div className="diverse-services">
          <h2>Premium Mobile App Design & Development</h2>
          <p>
            We have mastered enterprise mobile development for over a decade. By maintaining deep integration
            with active operating system protocols, our mobile architectures excel in security, offline capability,
            and pixel-perfect rendering for professional, widescreen environments.
          </p>
        </div>
      </section>

    </div>
    </div>
  );
};

export default MobileDeveloper;
